import { IProcessedStyleSet } from '@fluentui/merge-styles';
import { Spinner, SpinnerSize, mergeStyles } from '@fluentui/react';
import { IIconRecord } from '@fluentui/style-utilities';
import * as React from 'react';
import { RefObject } from 'react';
import {
  getSourceAppOrAppVerQueryString,
  getOmnichannelChatLinkTemplate,
  getOmnichannelContext,
} from '../../../../../web/utils/statelessUtil';
import { AutomationIds } from '../../../../models/constants/automationIds';
import { Environment, RDSubwayNavMarginBottom, SourceApps, URLs } from '../../../../models/constants/constants';
import { getBrowserType, isDfMRequest, shallowEquals, getRequestLocale } from '../../../../utilities';
import { SubwayNav, ISubwayNavNodeProps, ISubwayNavNodeStyles, SubwayNavNodeState } from '../../controls/SubwayNav';
import { generateRandomId } from '../../controls/SubwayNav/Wizard.utils';
import {
  FreelanceRequest,
  RequestModality,
  RequestState,
  RequestStatusStep,
  RequestStepsSchema,
} from '../../models/requestModels';
import {
  getRequestSectionStyles,
  getRequestSectionClassNames,
  getSubwayNavStyles,
  getSubwayProgressBarStyles,
  IRequestSectionStyles,
  getSubwayNavSpinnerStyles,
} from '../../styles/requestDetailsStyles';
import { IStyledComponent } from '../../styles/themesUI';
import { RequestStep } from './RequestStep';
import { getString } from '../../../../../web/utils/stringLoader';
import { httpUtils } from '../../../../../web/common/apis/httpUtils';
import { addLogs } from '../../../../../../omnichannel-live-chat-widget/apis/emerald.apis';

interface IStatusProps {
  request: FreelanceRequest;
  requestSteps: RequestStepsSchema[];
  isOrgId: boolean;
  omnichannelThemeName: string;
  isClientApp: boolean;
  openInAppOmnichannelChatInBrowser: boolean;
  market: string;
  environment: Environment;
  sourceApp: string;
  appVer: string;
  isGetRequestPending: boolean;
  correlationId: string;
}

type IRequestStatusProps = IStatusProps & IStyledComponent<IRequestSectionStyles>;

export class RequestStatus extends React.PureComponent<IRequestStatusProps> {
  private subwayNavId = '';
  private subwayContainer = React.createRef() as RefObject<HTMLDivElement>;
  private isIE = false;

  private sectionClassNames = getRequestSectionClassNames(getRequestSectionStyles, {
    theme: this.props.theme,
  });

  public componentDidMount = (): void => {
    if (this.props.request) {
      this.subwayNavId = `subwayNav-${this.props.request.ParatureTicketNumber}`;
      const browserType = getBrowserType();
      this.isIE = browserType && browserType.toLowerCase().indexOf('msie') !== -1;
      if (this.isIE) {
        this._getSubWayProgressBarForIE();
      }
    }
  };

  public render(): React.ReactNode {
    if (!this.props.request) {
      return null;
    }

    return (
      <>
        <div
          data-automation-id={AutomationIds.RequestStatusCard}
          className={this.sectionClassNames.sectionContainer}
          role="list"
        >
          <div
            ref={this.subwayContainer}
            id={this.subwayNavId}
            aria-hidden={true}
            className={this.sectionClassNames.contentContainer}
          >
            {this.props.isGetRequestPending ? (
              <Spinner
                key={'updatingrequest-spinner'}
                size={SpinnerSize.medium}
                theme={this.props.theme}
                styles={getSubwayNavSpinnerStyles()}
              />
            ) : (
              <SubwayNav styles={getSubwayNavStyles(this.props, this.isIE)} steps={this._stepsList()} />
            )}
          </div>
        </div>
      </>
    );
  }

  /**
   * IE 7 and below don't support ::before and ::after pseudo styles which are needed for SubwayNav progress bars
   * This method implements the progress bar using JS
   */
  private _getSubWayProgressBarForIE = () => {
    const { request } = this.props;
    if (this.subwayContainer && request) {
      const requestState = RequestState[request.State] || RequestState.Unknown;
      const uls = this.subwayContainer.current.getElementsByTagName('ul');
      if (uls && uls.length > 0) {
        const ul = uls[0];
        const listItems = ul.getElementsByTagName('li');
        if (listItems && listItems.length > 0) {
          const issueResolvedLi = listItems[2];
          let topOfIssueResolvedLi = issueResolvedLi ? issueResolvedLi.offsetTop : 0;
          const captionRoot = issueResolvedLi.querySelectorAll('[class^=captionRoot]')[0] as HTMLDivElement;
          topOfIssueResolvedLi = captionRoot ? topOfIssueResolvedLi + captionRoot.offsetTop : topOfIssueResolvedLi;
          // trim progress line
          switch (requestState) {
            case RequestState.Acknowledged:
            case RequestState.Active:
            case RequestState.Delayed:
              // trim progress line, we don't need trailing line after the last circle if the issue is not resolved
              topOfIssueResolvedLi = topOfIssueResolvedLi - RDSubwayNavMarginBottom;
              break;
            default:
              break;
          }
          const styles = getSubwayProgressBarStyles(
            this.props,
            `${topOfIssueResolvedLi}px`,
            `${this._subwayAfterHeight(listItems)}px`
          );
          const before = document.createElement('small');
          before.className = 'beforeIE ' + mergeStyles(styles.before);
          const after = document.createElement('small');
          after.className = 'afterIE ' + mergeStyles(styles.after);
          ul.appendChild(before);
          ul.appendChild(after);
        }
      }
    }
  };

  private _subwayAfterHeight = (listItems: HTMLCollectionOf<HTMLLIElement>): number => {
    let afterHeight = 0;
    const { request } = this.props;
    if (!listItems && !request) {
      return 0;
    }
    switch (RequestState[request.State]) {
      case RequestState.Active:
      case RequestState.Delayed:
        afterHeight = listItems[1] ? listItems[1].offsetTop : 0;
        break;
      case RequestState.Acknowledged:
        afterHeight = listItems[2] ? listItems[2].offsetTop : 0;
        break;
      default:
        break;
    }
    // trim progress line, we don't need trailing line after the last circle if the issue is not resolved
    if (afterHeight > 0) {
      afterHeight = afterHeight - RDSubwayNavMarginBottom;
    }
    return afterHeight;
  };

  private _stepsList = (): ISubwayNavNodeProps[] =>
    this.props.requestSteps.map((step: RequestStepsSchema) => {
      const stepId = generateRandomId();
      return {
        id: stepId,
        label: step.Title,
        role: 'listitem',
        /**
         * in windows 11, admin controls is wrapping steps inside a button,
         * if onClickStep is not provided and if disabled is set to true, the parent button swallows the child's onClick
         * passing onClickStep to parent and checking event.target to open link
         *  */
        disabled: step.Id !== RequestStatusStep.Opened && step.Id !== RequestStatusStep.Acknowledged,
        onClickStep: this._onChatButtonClick,
        onRenderStep: (
          props: ISubwayNavNodeProps,
          classNames: IProcessedStyleSet<ISubwayNavNodeStyles>,
          iconRecord?: IIconRecord,
          defaultOnRenderStepIcon?: ISubwayNavNodeProps['onRenderStepIcon']
        ): JSX.Element => {
          return (
            <>
              <div className={this.sectionClassNames.requestSteps} key={`stepIcon_${stepId}`}>
                {defaultOnRenderStepIcon!(props, classNames, iconRecord)}
              </div>
              <div className={classNames.spacer} />
              {shallowEquals(this.props.request!.Modality, RequestModality.Chat) &&
              (step.Id === RequestStatusStep.Opened || step.Id === RequestStatusStep.Acknowledged) ? (
                <RequestStep
                  theme={this.props.theme}
                  key={`status_${stepId}`}
                  data={step}
                  subwayNavClassNames={classNames}
                  themedClassNames={this.sectionClassNames}
                  onChatButtonClick={this._onChatButtonClick}
                />
              ) : (
                <RequestStep
                  theme={this.props.theme}
                  key={`status_${stepId}`}
                  data={step}
                  subwayNavClassNames={classNames}
                  themedClassNames={this.sectionClassNames}
                  statusMessage={this._getStepStatusMessage(step.Id)}
                />
              )}
            </>
          );
        },
        state: step.Active ? SubwayNavNodeState.Completed : SubwayNavNodeState.Current,
      };
    });

  private _onChatButtonClick = (e?: any): void => {
    if (this._shouldOpenChatWindow(e && e.target)) {
      const useOmnichannel: boolean = isDfMRequest(this.props.request.RequestId);
      let openTarget = '_blank';

      let chatLink: string = '';
      if (useOmnichannel) {
        const ocLink = getOmnichannelChatLinkTemplate(
          this.props.environment,
          this.props.openInAppOmnichannelChatInBrowser
        );
        const requestLocale = getRequestLocale(this.props.request);
        chatLink = ocLink
          .replace('{0}', !!requestLocale ? requestLocale : this.props.market)
          .replace('{1}', this.props.omnichannelThemeName)
          .replace(
            '{2}',
            getSourceAppOrAppVerQueryString(
              this.props.request.Source,
              this.props.isClientApp,
              this.props.openInAppOmnichannelChatInBrowser,
              this.props.appVer
            )
          )
          .replace('{3}', encodeURIComponent(JSON.stringify(getOmnichannelContext(this.props.request))))
          .replace('{4}', httpUtils.getEmsSessionId());
      } else {
        chatLink = URLs.RaveChatWidgetLink.replace('{0}', this.props.request.Source)
          .replace('{1}', this.props.request.RequestId)
          .replace('{2}', this.props.isOrgId ? '&isaad=true' : '');
      }

      if (
        (this.props.isClientApp && !(useOmnichannel && this.props.openInAppOmnichannelChatInBrowser)) ||
        this.props.request.Source === 'SignUp' ||
        this.props.request.Source === 'SmbHub' ||
        this.props.request.Source === 'VsbHub' ||
        this.props.sourceApp === SourceApps.Teams ||
        this.props.sourceApp === SourceApps.Teams2 ||
        this.props.sourceApp === SourceApps.TeamsAdmin ||
        this.props.sourceApp === SourceApps.TeamsAdmin2
      ) {
        openTarget = '_self';
      }

      addLogs({
        event: 'OpenChatLinkFromRequestStatus',
        chatLink: chatLink,
        openTarget: openTarget,
      }, this.props.correlationId);
      
      window.open(chatLink, openTarget);
    }
  };

  private _shouldOpenChatWindow = (targetElm?: HTMLButtonElement, depth?: number): boolean => {
    if (!targetElm) {
      return false;
    }
    /** fabric primary button wraps text with 3 span elements,
     * checking target id is equal to RequestStepStartChat is not enough because target could be child of button
     *  we can't set an Id on those elements so we'll check if the parent button element with Id = 'RequestStepStartChat' exists by going up 5 levels just to be safe
     * */
    depth = depth !== undefined ? depth : 5;
    if (depth <= 0) {
      return false;
    }
    const targetId = targetElm.getAttribute('id') || '';
    const startChatId = 'RequestStepStartChat';
    if (targetId === startChatId) {
      return true;
    } else {
      if (targetElm.closest && typeof targetElm.closest === 'function') {
        const parent = targetElm.closest(`#${startChatId}`);
        return !!parent;
      } else {
        /** element.closest doesn't exist in IE, workaround */
        const parent = targetElm.parentElement;
        if (!parent) {
          return false;
        }
        depth -= 1;
        return this._shouldOpenChatWindow(targetElm, depth);
      }
    }
  };

  private _getStepStatusMessage = (id: number): string => {
    // Sets a status for screen readers when a step is not yet completed
    if (id === 2) {
      return getString('requestStatus_agentPendingStep');
    } else if (id === 6) {
      return getString('requestStatus_issueResolutionPendingStep');
    } else {
      return String();
    }
  };
}
